import { useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Form } from "~/atoms/form";
import { Button } from "~/atoms/button";
import { PlatformRole } from "~/config/types";
import { type DocumentDetails } from "~/api/types";
import { useDocumentEditForm } from "~/hooks/useDocumentEditForm";
import { useDocumentLinking } from "~/hooks/useDocumentLinking";
import { CenterSpinner } from "~/atoms/spinner";
import { MetadataSection } from "~/molecules/DocumentMetadataSections";
import { DocumentCategoryField } from "~/molecules/document-fields/DocumentCategoryField";
import { DocumentFieldsGuard } from "~/molecules/document-fields/DocumentFieldsGuard";
import { DocumentLinkField } from "~/molecules/document-fields/DocumentLinkField";
import { DocumentManagersFields } from "~/molecules/document-fields/DocumentManagersFields";
import { DocumentReferenceNumberField } from "~/molecules/document-fields/DocumentReferenceNumberField";
import { DocumentScheduledReviewFields } from "~/molecules/document-fields/DocumentScheduledReviewFields";
import { DocumentTierField } from "~/molecules/document-fields/DocumentTierField";
import { TagForm } from "~/molecules/TagForm";
import { LegalEntitySelectorWrapper } from "~/helpers/legalEntities";
import { TagsModal } from "~/organisms/TagsModal";
import { type UpdateDocumentData, updateDocument } from "~/api/v2/requests";
import { toast } from "react-toastify";
import { useEffect, useMemo, useState } from "react";
import { UnsavedChangesAlert } from "~/molecules/UnsavedChangesAlert";
import { useUserContext } from "~/hooks/useUserContext";

const formId = "document-metadata-form";

interface DocumentMetadataEditProps {
  document: DocumentDetails;
  onSave: () => void;
  onCancel: () => void;
  onUnsavedChangesChange?: (hasUnsavedChanges: boolean) => void;
}

export const DocumentMetadataEdit = ({
  document,
  onSave,
  onCancel,
  onUnsavedChangesChange,
}: DocumentMetadataEditProps) => {
  const { t } = useTranslation();
  const { form, filterUnchangedValues } = useDocumentEditForm({ document });
  const [isCancelAlertOpen, setIsCancelAlertOpen] = useState(false);
  const { user } = useUserContext();

  const [
    selectedTags,
    selectedTierId,
    selectedParentDocuments,
    selectedChildDocuments,
  ] = useWatch({
    control: form.control,
    name: ["tags", "tierId", "parentDocuments", "childDocuments"],
  });

  const formValues = useWatch({ control: form.control });
  const hasUnsavedChanges = useMemo(() => {
    if (!formValues) {
      return false;
    }

    const filteredValues = filterUnchangedValues(
      formValues as Parameters<typeof filterUnchangedValues>[0],
    );

    return Object.keys(filteredValues).length > 0;
  }, [filterUnchangedValues, formValues]);

  const { parentContext, childContext, isLoading } = useDocumentLinking({
    document,
    selectedTierId,
    selectedParentDocuments,
    selectedChildDocuments,
  });

  useEffect(() => {
    onUnsavedChangesChange?.(hasUnsavedChanges);
  }, [hasUnsavedChanges, onUnsavedChangesChange]);

  const handleSaveDetails = async (values: UpdateDocumentData) => {
    try {
      await updateDocument(document.id, values);
      onSave();
      toast.success(t(($) => $.toasts.success.documentUpdated));
      return true;
    } catch (error) {
      console.error("Error updating document:", error);
      toast.error(t(($) => $.toasts.error.generic));
      return false;
    }
  };

  const handleFormSubmit = async (
    values: Parameters<typeof filterUnchangedValues>[0],
  ) => {
    const filteredValues = filterUnchangedValues(values);
    if (Object.keys(filteredValues).length > 0) {
      const saveSucceeded = await handleSaveDetails(filteredValues);
      if (!saveSucceeded) {
        return;
      }

      form.reset(values);
      onUnsavedChangesChange?.(false);
      onCancel();
    } else {
      form.reset();
      onUnsavedChangesChange?.(false);
      onCancel();
    }
  };

  const handleCancel = () => {
    if (hasUnsavedChanges) {
      setIsCancelAlertOpen(true);
      return;
    }

    form.reset();
    onUnsavedChangesChange?.(false);
    onCancel();
  };

  const handleLeaveWithoutSaving = () => {
    form.reset();
    onUnsavedChangesChange?.(false);
    onCancel();
  };

  if (isLoading) {
    return <CenterSpinner />;
  }

  return (
    <>
      <UnsavedChangesAlert
        hasUnsavedChanges={hasUnsavedChanges}
        manualOpen={isCancelAlertOpen}
        onManualOpenChange={setIsCancelAlertOpen}
        onManualLeave={handleLeaveWithoutSaving}
        title={t(($) => $.commons.unsavedChanges.documentMetadata.title)}
        description={t(
          ($) => $.commons.unsavedChanges.documentMetadata.description,
        )}
        cancelLabel={t(($) => $.commons.unsavedChanges.continueEditing)}
        confirmLabel={t(($) => $.commons.unsavedChanges.discardChanges)}
      />
      <Form {...form}>
        <form
          id={formId}
          onSubmit={form.handleSubmit(handleFormSubmit)}
          className="flex flex-col gap-4"
        >
          <MetadataSection title={t(($) => $.commons.managers.title)}>
            <DocumentManagersFields />
          </MetadataSection>

          <MetadataSection title={t(($) => $.commons.classification.title)}>
            <div className="flex flex-col gap-2">
              <DocumentCategoryField />
              <DocumentFieldsGuard>
                <DocumentTierField />
              </DocumentFieldsGuard>
              <DocumentReferenceNumberField />
            </div>
          </MetadataSection>

          <MetadataSection title={t(($) => $.commons.document.recurringReview)}>
            <DocumentScheduledReviewFields />
          </MetadataSection>

          <MetadataSection
            title={t(($) => $.commons.document.tags)}
            action={
              user.role === PlatformRole.coordinator ||
              user.role === PlatformRole.superAdmin ? (
                <TagsModal
                  trigger={
                    <Button variant="outline" size="sm">
                      {t(($) => $.modal.tags.manageTags)}
                    </Button>
                  }
                />
              ) : undefined
            }
          >
            <TagForm
              selectedTags={selectedTags}
              onTagsChange={(tags: string[]) => form.setValue("tags", tags)}
            />
            <LegalEntitySelectorWrapper form={form} documentId={document.id} />
          </MetadataSection>

          <MetadataSection title={t(($) => $.commons.document.links)}>
            {parentContext && (
              <DocumentLinkField
                name="parentDocuments"
                title={parentContext.tierName}
                options={parentContext.options}
              />
            )}
            {childContext && (
              <DocumentLinkField
                name="childDocuments"
                title={childContext.tierName}
                options={childContext.options}
              />
            )}
            {parentContext == null && childContext == null && (
              <p className="text-muted-foreground text-sm">
                {t(($) => $.commons.document.noLinkingOptions)}
              </p>
            )}
          </MetadataSection>
        </form>
      </Form>
      <div className="bg-card border-border sticky bottom-0 border-t py-6">
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            className="flex-1"
            onClick={handleCancel}
          >
            {t(($) => $.commons.buttons.cancel)}
          </Button>
          <Button type="submit" form={formId} className="flex-1">
            {t(($) => $.commons.buttons.save)}
          </Button>
        </div>
      </div>
    </>
  );
};
